/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import Image from '@atlaskit/image';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { onOpenChatButtonClick } from '../../../ai-answer-dialog/on-open-chat-button-click';
import { onSendChatInput } from '../../../ai-answer-dialog/on-send-chat-input';
import { type commonAttributesType } from '../../../ai-answer-dialog/types';
import { SainAction } from '../../../common/controller/analytics/hooks/sain-action';
import { useSAINUsageCriteriaMet } from '../../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../../common/controller/analytics/use-analytics';
import { SAINFeatureControl } from '../../../common/utils/sain-feature-control';

import RovoIcon from './assets/Rovo.svg';
import { messages } from './messages';

const styles = cssMap({
	// Outer wrapper that owns the show/hide animation. The optional title and the
	// input box both live inside it, so they fade out together as one unit — a
	// host doesn't need to separately animate or unmount the title.
	wrapper: {
		width: '100%',
		zIndex: 500,
		boxSizing: 'border-box',
		transition: 'display 1s allow-discrete, opacity 0.8s ease-out',
	},
	wrapperShownStyles: {
		display: 'block',
		opacity: 1,
	},
	wrapperHiddenStyles: {
		display: 'none',
		opacity: 0,
	},
	root: {
		borderRadius: token('radius.xlarge'),
		width: '100%',
		paddingTop: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.150'),
		backgroundColor: token('elevation.surface.sunken'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		boxSizing: 'border-box',
		overflow: 'hidden',
	},
	inputIconButtonWrapperStyles: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 600,
	},
	// Enhanced styles (gate on)
	enhancedRoot: {
		borderRadius: token('radius.xlarge'),
		width: '100%',
		zIndex: 500,
		paddingTop: token('space.200'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.150'),
		backgroundColor: token('elevation.surface'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		boxSizing: 'border-box',
		overflow: 'hidden',
		position: 'relative',
		transition: 'display 1s allow-discrete, opacity 0.8s ease-out',
	},
	sendButtonOverlay: {
		position: 'absolute',
		bottom: token('space.150'),
		right: token('space.150'),
	},
});

export type OpenChatInputBoxProps = {
	/**
	 * The user's search query. When a SAIN answer is also present it is
	 * forwarded to Rovo Chat as prior context (and seeds the chat name).
	 * Falls back to `useAIAnswerContext().commonAttributes.query` when omitted.
	 */
	query?: string;
	/**
	 * The current search session id, sent as `sourceId` on the `chat-new`
	 * payload. Falls back to
	 * `useAIAnswerContext().commonAttributes.searchSessionId` when omitted.
	 */
	searchSessionId?: string;
	/**
	 * Controls whether the box renders. Falls back to
	 * `useAIAnswerContext().showChatFollowUps` when omitted; hosts rendering
	 * outside `AIAnswerContext` should pass `true`.
	 */
	isVisible?: boolean;
	/**
	 * The `source` field on the `chat-new` payload (also used in analytics).
	 * Defaults to `'sain'`; FPS hosts should pass `'fps'`.
	 */
	source?: 'sain' | 'fps';
	/**
	 * The analytics attributes spread onto the fired events. Hosts that render
	 * this box outside an `AIAnswerContext` provider (e.g. FPS results) must
	 * supply these, otherwise it falls back to
	 * `useAIAnswerContext().commonAttributes`.
	 */
	commonAttributes?: commonAttributesType;
	/**
	 * Called when the box's visibility changes — notably with `false` after a
	 * prompt is submitted (the box hides itself). Lets hosts keep sibling
	 * chrome in sync, or remove the (already faded-out) box from the tree.
	 */
	onVisibilityChange?: (isVisible: boolean) => void;
	/**
	 * Optional heading rendered inside the box, above the input. Because it
	 * lives inside the same element that fades out, it shares the box's
	 * show/hide transition automatically — hosts don't need to animate or
	 * unmount a separate title. Omitted by SAIN-hosted usage.
	 */
	title?: string;
	/**
	 * Called once the box has finished playing its fade-out (the `opacity`
	 * transition ending after it hides itself). Lets hosts react exactly when the
	 * exit animation completes — e.g. to unmount a floating wrapper only after the
	 * fade is visible, instead of guessing the duration with a timer.
	 */
	onHideTransitionEnd?: () => void;
};

export const OpenChatInputBox = ({
	query: queryProp,
	searchSessionId: searchSessionIdProp,
	isVisible,
	source = 'sain',
	commonAttributes: commonAttributesProp,
	onVisibilityChange,
	title,
	onHideTransitionEnd,
}: OpenChatInputBoxProps = {}): JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const publish = usePublish('ai-mate');

	const [showOpenChatInput, setShowOpenChatInput] = useState(true);
	const [inputValue, setInputValue] = useState('');

	const { commonAttributes: contextCommonAttributes, showChatFollowUps } = useAIAnswerContext();
	const commonAttributes = commonAttributesProp ?? contextCommonAttributes;
	const query = queryProp ?? commonAttributes.query;
	const searchSessionId = searchSessionIdProp ?? commonAttributes.searchSessionId;
	const { answerString } = commonAttributes;
	// Only apply the improved SAIN layout when source is 'sain' — the FPS entry
	// point (fps-open-chat-input-box) reuses this component and must keep its
	// own appearance regardless of the gate state.
	const isImprovedChatOptionsEnabled =
		source === 'sain' && SAINFeatureControl.isImprovedChatOptionsEnabled();
	const shouldRender = isVisible ?? showChatFollowUps;

	const isSendDisabled = !inputValue.trim();

	const fireSendAnalytics = useCallback(() => {
		fireAnalyticsEvent(
			onSendChatInput({
				chatInputQuery: inputValue,
				...commonAttributes,
			}),
		);
		fireAnalyticsEvent(onOpenChatButtonClick({ ...commonAttributes }));
		fireSAINUsageEvent({ metCriteria: SainAction.OPEN_CHAT_CLICKED, commonAttributes });
	}, [fireAnalyticsEvent, fireSAINUsageEvent, inputValue, commonAttributes]);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (inputValue.trim() && event.key === 'Enter') {
			event.preventDefault();
			fireSendAnalytics();
			sendToChat();
		}
	};

	const sendToChat = useCallback(() => {
		// Carry the prior turn (and seed the chat name) only when there is a
		// SAIN answer to forward. This keeps the box future-proof: when no
		// answer is present (e.g. FPS results today) the user lands in a clean
		// new chat with only what they typed, and once an answer is available
		// the host simply passes `query` + `answerString` to carry the context
		// — no change needed here.
		const hasAnswerToCarry = Boolean(query && answerString);

		publish({
			type: 'chat-new',
			source,
			data: {
				...(hasAnswerToCarry ? { name: query.substring(0, 30) } : {}),
				dialogues: hasAnswerToCarry
					? [{ human_message: { content: query }, agent_message: { content: answerString } }]
					: [],
				prompt: inputValue,
				sourceId: searchSessionId,
			},
		});

		setShowOpenChatInput(false);
		// Notify the host at the point the box hides itself (after a prompt is
		// submitted), so sibling chrome (e.g. a title rendered above the box by
		// the host) can hide together with it. We deliberately don't emit an
		// initial `true` on mount — hosts own the box's starting visibility.
		onVisibilityChange?.(false);
	}, [inputValue, query, searchSessionId, publish, answerString, source, onVisibilityChange]);

	const handleButtonClick = useCallback(() => {
		fireSendAnalytics();
		sendToChat();
	}, [fireSendAnalytics, sendToChat]);

	const handleTransitionEnd = useCallback(
		(event: React.TransitionEvent<HTMLDivElement>) => {
			// Fire only when the box's own fade-out (opacity) finishes after it has hidden itself
			if (
				!showOpenChatInput &&
				event.propertyName === 'opacity' &&
				event.target === event.currentTarget
			) {
				onHideTransitionEnd?.();
			}
		},
		[showOpenChatInput, onHideTransitionEnd],
	);

	if (!shouldRender) {
		return null;
	}

	if (isImprovedChatOptionsEnabled) {
		return (
			<Box
				xcss={cx(
					styles.enhancedRoot,
					showOpenChatInput ? styles.wrapperShownStyles : styles.wrapperHiddenStyles,
				)}
				// TODO: replace with elevation.shadow.aiinput token once available in the design system
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/design-system/ensure-design-token-usage
				style={{ boxShadow: '0 -2px 50px 8px rgba(30, 31, 33, 0.08)' }}
				onTransitionEnd={handleTransitionEnd}
			>
				<TextArea
					aria-label={formatMessage(messages.ai_answer_open_chat_input_box_ask_rovo)}
					placeholder={formatMessage(messages.ai_answer_open_chat_input_box_ask_rovo)}
					value={inputValue}
					onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setInputValue(e.target.value)}
					onKeyDown={handleKeyDown}
					appearance="none"
					minimumRows={2}
					testId="lumina-input-to-chat-text-area"
				/>
				<Box xcss={styles.sendButtonOverlay}>
					<IconButton
						appearance="primary"
						isDisabled={isSendDisabled}
						icon={ArrowUpIcon}
						label={formatMessage(messages.ai_answer_open_chat_input_box_send_query)}
						onClick={handleButtonClick}
						testId="lumina-input-to-chat-submit"
					/>
				</Box>
			</Box>
		);
	}

	return (
		<Box
			xcss={cx(
				styles.wrapper,
				showOpenChatInput ? styles.wrapperShownStyles : styles.wrapperHiddenStyles,
			)}
			onTransitionEnd={handleTransitionEnd}
		>
			<Stack space="space.150">
				{title ? (
					<Text size="large" weight="medium">
						{title}
					</Text>
				) : null}
				<Box xcss={styles.root}>
					<Inline alignBlock="center">
						<Box xcss={styles.inputIconButtonWrapperStyles}>
							<Image src={RovoIcon} alt="" />
						</Box>
						<TextArea
							placeholder={formatMessage(messages.ai_answer_open_chat_input_box_ask_rovo)}
							value={inputValue}
							onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
								setInputValue(e.target.value)
							}
							onKeyDown={handleKeyDown}
							appearance="none"
							minimumRows={1}
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
							style={{ paddingTop: '9px' }}
							testId="lumina-input-to-chat-text-area"
						/>
						<Box xcss={styles.inputIconButtonWrapperStyles}>
							<IconButton
								appearance="primary"
								isDisabled={isSendDisabled}
								shape="circle"
								icon={ArrowUpIcon}
								label={formatMessage(messages.ai_answer_open_chat_input_box_send_query)}
								onClick={handleButtonClick}
								testId="lumina-input-to-chat-submit"
							/>
						</Box>
					</Inline>
				</Box>
			</Stack>
		</Box>
	);
};
